/**
 * RowActionsMenu — kolumna „Akcje" jako menu pod trzema kropkami.
 *
 * Zamiast renderować wszystkie ikony akcji w rzędzie (edytuj / transfer /
 * usuń / własne przyciski), zwija je do jednego przycisku z ikoną
 * `MoreVertical`; po kliku otwiera dropdown (portal do body) z listą pozycji.
 * Zamykany klikiem poza menu.
 *
 * Generyczny: każda pozycja to `{ label, onSelect, icon?, danger?, disabled? }`.
 * Caller składa listę pozycji (akcje standardowe + własne przyciski row_actions).
 * Wzorzec wyjęty z `ModuleRecordsPage` (lokalny `RecordActionsMenu`) — teraz
 * współdzielony przez listy systemowe i moduły własne.
 */

import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { type LucideIcon, MoreVertical } from 'lucide-react';

export interface RowActionItem {
  /** Klucz Reacta + identyfikator pozycji. */
  key: string;
  label: string;
  icon?: LucideIcon;
  onSelect: () => void;
  /** Czerwona pozycja (akcja destrukcyjna, np. usuń). */
  danger?: boolean;
  disabled?: boolean;
  /**
   * Renderuj separator PRZED tą pozycją — do oddzielenia akcji per-rekord
   * (edytuj/przenieś/usuń) od akcji z katalogu list (PHASE 25: akcje na jednym
   * rekordzie pod dividerem).
   */
  dividerBefore?: boolean;
  testId?: string;
}

export function RowActionsMenu({
  items,
  ariaLabel,
  testId,
}: {
  items: RowActionItem[];
  ariaLabel?: string;
  testId?: string;
}): React.ReactElement {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState<{ top: number; left: number } | null>(null);
  const btnRef = useRef<HTMLButtonElement>(null);

  function openMenu(e: React.MouseEvent) {
    e.stopPropagation();
    const rect = btnRef.current?.getBoundingClientRect();
    if (rect) setPos({ top: rect.bottom + 4, left: rect.right - 180 });
    setOpen(true);
  }

  useEffect(() => {
    if (!open) return;
    const handler = () => setOpen(false);
    document.addEventListener('mousedown', handler, { once: true });
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  const menu =
    open && pos
      ? createPortal(
          <div
            style={{ position: 'fixed', top: pos.top, left: pos.left, zIndex: 9999, minWidth: 180 }}
            className="rounded-md border border-border-primary bg-bg-primary py-1 shadow-lg"
            role="menu"
          >
            {items.map((item) => {
              const Icon = item.icon;
              return (
                <div key={item.key}>
                  {item.dividerBefore && (
                    <div className="my-1 border-t border-border-primary" role="separator" />
                  )}
                  <button
                    type="button"
                    role="menuitem"
                    disabled={item.disabled}
                    onMouseDown={(e) => {
                      e.stopPropagation();
                      setOpen(false);
                      item.onSelect();
                    }}
                    className={
                      'flex w-full items-center gap-2 px-3 py-2 text-left text-sm disabled:cursor-not-allowed disabled:opacity-40 ' +
                      (item.danger
                        ? 'text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20'
                        : 'text-text-primary hover:bg-bg-tertiary')
                    }
                    data-testid={item.testId}
                  >
                    {Icon ? <Icon size={14} aria-hidden="true" /> : null}
                    {item.label}
                  </button>
                </div>
              );
            })}
          </div>,
          document.body,
        )
      : null;

  return (
    <>
      <button
        ref={btnRef}
        type="button"
        onClick={openMenu}
        aria-label={ariaLabel ?? t('common.actions')}
        aria-expanded={open}
        className="rounded-md p-1.5 text-text-muted hover:bg-bg-tertiary hover:text-text-primary"
        data-testid={testId}
      >
        <MoreVertical size={16} aria-hidden="true" />
      </button>
      {menu}
    </>
  );
}
